'use client';

import { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import Link from 'next/link';

interface SearchablePost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
}

export function BlogSearch({ posts }: { posts: SearchablePost[] }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const results =
    query.length >= 2
      ? posts.filter(
          (p) =>
            p.title.toLowerCase().includes(query.toLowerCase()) ||
            p.excerpt.toLowerCase().includes(query.toLowerCase())
        ).slice(0, 6)
      : [];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-lg mx-auto mt-8">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search articles..."
          className="w-full rounded-full bg-white/10 border border-white/15 pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 transition-all"
        />
      </div>

      {open && results.length > 0 && (
        <div className="absolute top-full mt-2 w-full rounded-xl bg-white dark:bg-slate-900 border border-border/60 shadow-2xl overflow-hidden z-50">
          {results.map((post) => (
            <Link
              key={post.slug}
              href={`/blog/${post.slug}`}
              onClick={() => {
                setOpen(false);
                setQuery('');
              }}
              className="block px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors border-b border-border/40 last:border-b-0"
            >
              <p className="text-sm font-medium text-slate-900 dark:text-white line-clamp-1">
                {post.title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {post.category} &middot; {post.excerpt}
              </p>
            </Link>
          ))}
        </div>
      )}

      {open && query.length >= 2 && results.length === 0 && (
        <div className="absolute top-full mt-2 w-full rounded-xl bg-white dark:bg-slate-900 border border-border/60 shadow-2xl p-4 z-50">
          <p className="text-sm text-muted-foreground text-center">
            No articles found for &ldquo;{query}&rdquo;
          </p>
        </div>
      )}
    </div>
  );
}
